import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import {
  getOrgPreferenceKey,
  PreferenceKey,
  PreferenceScope,
} from '../../../../../data-models/preferences-v3.data-model';
import { defaultCurrency } from '../../../../../services/state/AppConfigStateJ';
import { companyStateJ } from '../../../../../services/state/CompanyStateJ';
import { CurrencyCode } from '../../../../../util/currency-util';
import { getForesightStore } from '../../../../../util/jotai-store';
import { preferenceAtom } from '../../../../../util/preferences-service-v3';

/**
 * FD-3875 financials FX display currency. Phase 1 offers two options per
 * company: its reported (native) currency and USD. Conversion runs at read time
 * on the BE (`?currencyCode=`); this state only tracks the selection.
 */

// Stored per-user, per-company selection. `undefined` until the user picks one.
export const financialsCurrencyPrefAtom = atomFamily((companyId: number) => {
  const ref = {
    scope: PreferenceScope.USER,
    preferenceKey: getOrgPreferenceKey(PreferenceKey.financialsCurrency, companyId),
  };
  return preferenceAtom<CurrencyCode>(ref, ref);
});

// companyStateJ may hold an unresolved Promise; only read a settled company.
function nativeCurrencyOf(company: unknown): CurrencyCode | undefined {
  if (!company || company instanceof Promise) {
    return undefined;
  }
  const code = (company as { defaultCurrency?: string | null }).defaultCurrency;
  return code ? (code.toUpperCase() as CurrencyCode) : undefined;
}

// Reactive selection: stored pref, else the company's native currency, else USD.
export const selectedFinancialsCurrencyForIdAtom = atomFamily((companyId: number) =>
  atom(
    (get): CurrencyCode => {
      if (companyId <= 0) {
        return defaultCurrency;
      }
      return (
        get(financialsCurrencyPrefAtom(companyId)) ??
        nativeCurrencyOf(get(companyStateJ(companyId))) ??
        defaultCurrency
      );
    },
    (_get, set, next: CurrencyCode) => {
      if (companyId > 0) {
        set(financialsCurrencyPrefAtom(companyId), next);
      }
    }
  )
);

// The options offered: [native, USD], or just [USD] when native is USD/unknown.
export const financialsCurrencyOptionsForIdAtom = atomFamily((companyId: number) =>
  atom((get): CurrencyCode[] => {
    const native = companyId > 0 ? nativeCurrencyOf(get(companyStateJ(companyId))) : undefined;
    return !native || native === defaultCurrency ? [defaultCurrency] : [native, defaultCurrency];
  })
);

/**
 * Non-reactive resolver for the fetch path. Prefers the stored selection;
 * otherwise awaits the company so a non-USD company loads in its native
 * currency on first fetch instead of flashing USD before the company resolves.
 */
export async function resolveFinancialsCurrencyForFetch(companyId: number): Promise<CurrencyCode> {
  const store = getForesightStore();
  const stored = store.get(financialsCurrencyPrefAtom(companyId));
  if (stored) {
    return stored;
  }
  const company = await store.get(companyStateJ(companyId));
  return nativeCurrencyOf(company) ?? defaultCurrency;
}
